import { useEffect, useState } from 'react';

/**
 * Hook to handle scrolling to an anchor (hash) in the URL after the page is fully loaded.
 * Returns a boolean indicating whether the page has fully loaded.
 */
export function useScrollToAnchor(): boolean {
  const [isPageLoaded, setIsPageLoaded] = useState(false); // State to track if the page is fully loaded
  const [deferredHash, setDeferredHash] = useState<string | null>(null); // Stores the hash to scroll to

  // Listen for the page load event
  useEffect(() => {
    const handleLoad = () => {
      setIsPageLoaded(true); // Set the page as loaded
    };

    if (document.readyState === 'complete') {
      handleLoad(); // If the page is already loaded
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  // Capture the hash in the URL before the page is fully loaded
  useEffect(() => {
    if (!isPageLoaded && window.location.hash) {
      setDeferredHash(window.location.hash); // Save the hash for later use
    }
  }, [isPageLoaded]);

  // Scroll to the saved hash after the page is fully loaded
  useEffect(() => {
    if (isPageLoaded && deferredHash) {
      const targetElement = document.querySelector(deferredHash);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
        setDeferredHash(null); // Clear the hash after scrolling
      }
    }
  }, [isPageLoaded, deferredHash]);

  return isPageLoaded;
}
